<template>
    <div>
        <div v-if="isNoReports">
            <div class="text-center pa-5">
                <v-btn color="primary" @click="handleAddReport">Add report</v-btn>
            </div>
        </div>
        <div v-else>
            <v-tabs show-arrows v-model="activeReportTab" @change="onTabChange">
                <v-tabs-slider></v-tabs-slider>
                <v-tab v-for="internalReport in internalReports"
                       :key="internalReport.order">
                    Report {{ internalReport.order }}
                </v-tab>

                <v-btn v-if="canAddReport" class="mt-1 mr-0 pa-0" text icon color="primary" @click="handleAddReport">
                    <i class="fas fa-plus"></i>
                </v-btn>
                <v-btn v-if="canCopyReport" class="mt-1 ml-0 pa-0" text icon color="primary" @click="handleCopyButtonClick(currentInternalReport)">
                    <i class="fas fa-copy"></i>
                </v-btn>
            </v-tabs>
            <v-tabs-items v-model="activeReportTabItem">
                <v-tab-item
                        v-for="(internalReport, index) in internalReports"
                        :key="internalReport.order"
                >
                    <v-card flat>
                        <v-card-text class="text--primary">
                            <report :ref="buildReportComponentRef(internalReport.order)"
                                    v-if="index === activeInternalReportIndex"
                                    :report="internalReport"
                                    :caseData="caseData"
                                    :caseUnsavedChangesChecker="caseUnsavedChangesChecker"
                                    @create-report="handleCreateReport"
                                    @update-report="handleUpdateReport"
                                    @delete-report="handleDeleteReport"
                            ></report>
                            <div v-else></div>
                        </v-card-text>
                    </v-card>
                </v-tab-item>
            </v-tabs-items>
        </div>
    </div>

</template>

<script>
    import _ from 'lodash'
    import Report from './Report'
    import {mapActions} from 'vuex'

    export default {
        name: 'ReportsManager',
        props: {
            reports: Array,
            caseData: Object,
            caseUnsavedChangesChecker: Function
        },
        components: {
            Report
        },
        watch: {
            reports () {
                const data = this.initData()

                this.orderSequence = data.orderSequence,
                this.internalReports = data.internalReports
            }
        },
        data() {
            return {
                ...this.initData()
            }
        },
        computed: {
            isNoReports() {
                return _.get(this, 'internalReports', []).length === 0
            },
            canAddReport() {
                if (this.internalReports.length === 0) {
                    return true
                }

                const draftReports = _.filter(this.internalReports, (ir) => {
                    return !ir.id
                })

                return draftReports.length === 0
            },
            currentInternalReport() {
                if (this.activeInternalReportIndex < 0) {
                    return null
                }

                return this.internalReports[this.activeInternalReportIndex]
            },
            canCopyReport() {
              return this.currentInternalReport && this.currentInternalReport.id
            }
        },
        methods: {
            ...mapActions('pgtCases', {
                deleteReport: 'deleteReport'
            }),
            buildReportComponentRef(order) {
              return `report-${order}`
            },
            async handleAddReport() {
                // Если текущего заключения нет, просто добавим заключение
                if (!this.currentInternalReport) {
                    this.addEmptyReport()
                    return
                }

                const currentReportComponent = this.getCurrentReportComponent()

                if (!currentReportComponent) {
                    return
                }

                // Если текущее заключение есть - запросим подвтерждение несохраненных действий
                await currentReportComponent.doActionAfterReportUnsavedChangesCheck(this.addEmptyReport)
            },
            addEmptyReport() {
                this.addReport({
                    files: [],
                    samples: [],
                    specialists: [],
                    validated: false,
                    comment: null,
                    overrideReport: null,
                    snapshotDiff: null
                })
            },
            async addReport(report) {
                report.order = ++this.orderSequence
                this.internalReports.push(report)

                await this.$nextTick()
                this.activeInternalReportIndex = this.internalReports.length - 1
                this.activeReportTab = this.activeInternalReportIndex
                this.activeReportTabItem = this.activeInternalReportIndex
            },
            copyInternalReport(reportId) {
                const reportIndex = _.findIndex(this.internalReports, (ir) => {
                    return ir.id === reportId
                })

                if (reportIndex < 0) {
                    return false
                }

                const report = this.internalReports[reportIndex]

                this.addReport({
                    files: [],
                    samples: _.cloneDeep(report.samples),
                    comment: _.cloneDeep(report.comment),
                    specialists: [],
                    validated: false,
                    overrideReport: null,
                    snapshotDiff: null
                })

                return true
            },
            handleCreateReport(report) {
                this.$emit('create-report', report)
            },
            handleUpdateReport(report) {
                this.$emit('update-report', report)
            },
            initData() {
                let orderSequence = _.get(_.maxBy(this.reports, 'order'), 'order', 0)

                const internalReports = _.orderBy(_.cloneDeep(this.reports), 'order')
                const internalReportsLength = internalReports.length
                let lastInternalReportIndex = internalReportsLength - 1

                return {
                    orderSequence: orderSequence,
                    internalReports: internalReports,
                    activeInternalReportIndex: lastInternalReportIndex,
                    // Активный tab
                    activeReportTab: lastInternalReportIndex,

                    // Активное содержимое tab'а
                    activeReportTabItem: lastInternalReportIndex
                }
            },
            getCurrentReportComponent() {
                if (!this.currentInternalReport) {
                    return null
                }

                const currentReportComponentRef = this.buildReportComponentRef(this.currentInternalReport.order)
                return _.get(this.$refs[currentReportComponentRef], 0, null)
            },
            checkCurrentReportUnsavedChanges() {
                const currentReportComponent = this.getCurrentReportComponent()

                if (!currentReportComponent) {
                    return false
                }

                return currentReportComponent.checkReportUnsavedChanges()
            },
            async onTabChange(tab) {
                const currentReportComponent = this.getCurrentReportComponent()
                // Если нет текущего заключения поменяем вкладки как обычно
                if (!currentReportComponent) {
                    this.activeReportTab = tab
                    this.activeReportTabItem = tab
                    this.activeInternalReportIndex = tab
                    return
                }

                let unsavedChangesConfirmed = false

                // Проверим, есть ли несохраненные изменения
                await currentReportComponent.doActionAfterReportUnsavedChangesCheck( () => {
                    this.activeReportTab = tab
                    this.activeReportTabItem = tab
                    this.activeInternalReportIndex = tab
                    unsavedChangesConfirmed = true
                })

                // Если несохраненные изменения не подтверждены
                if (!unsavedChangesConfirmed) {
                    this.activeReportTab = this.activeReportTabItem
                }
            },
            async handleCopyButtonClick(report) {
                if (!report.id) {
                    return
                }

                await this.copyInternalReport(report.id)
            },
            async handleDeleteReport(report) {
                if (report.id) {
                    this.deleteReport(report.id)
                } else {
                    let reportIndex = _.findIndex(this.internalReports, (internalReport) => {
                        return internalReport.order === report.order
                    })

                    if (reportIndex < 0) {
                        return
                    }

                    this.internalReports.splice(reportIndex, 1)
                }

                this.orderSequence = _.get(_.maxBy(this.internalReports, 'order'), 'order', 0)

                const newIndex = this.internalReports.length - 1
                this.activeReportTab = newIndex
                this.activeReportTabItem = newIndex
                this.activeInternalReportIndex = newIndex
            }
        }
    }
</script>

<style scoped>

</style>